import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import SummaryCard from "./SummaryCard";
import ConfirmDialog from "./ConfirmDialog";
import Modal from "../../../../shared/Modal";
import api from "../../../../../utils/apiConfig";

import logger from '../../../../../utils/logger';

const getUnifiedInsuranceStatus = (insuranceData, noneActive) => {
  logger.info("getUnifiedInsuranceStatus called with:", {
    insuranceData,
    noneActive,
  });

  // If there's no insurance_status entry (noneActive is undefined), show "Not Started"
  if (noneActive === undefined) {
    return { label: "Not Started", color: "default" };
  }

  // If explicitly marked as having no active policies (is_active = false)
  if (noneActive === true) {
    return { label: "No Active Policies", color: "default" };
  }

  if (!insuranceData) {
    return { label: "Loading...", color: "default" };
  }

  const isPolicyActive = (policy) => {
    if (!policy) return false;
    return (
      policy.isActive !== false &&
      (!policy.endDate || new Date(policy.endDate) > new Date())
    );
  };

  const activeLifeInsurance = (insuranceData.lifeInsurance || []).filter(
    isPolicyActive,
  );
  const activeAdditionalCoverage = (
    insuranceData.additionalCoverage || []
  ).filter(isPolicyActive);
  const totalActivePolicies =
    activeLifeInsurance.length + activeAdditionalCoverage.length;

  logger.info("Active policy counts:", {
    activeLifeInsurance: activeLifeInsurance.length,
    activeAdditionalCoverage: activeAdditionalCoverage.length,
    totalActivePolicies,
    noneActive,
  });

  // If no policies and not explicitly marked as none active, show "Not Started"
  if (totalActivePolicies === 0 && noneActive !== true) {
    return { label: "Not Started", color: "default" };
  }

  // If there are active policies, show the count
  if (totalActivePolicies > 0) {
    return {
      label: `Active Coverages (${totalActivePolicies})`,
      color: "success",
    };
  }

  // Default to "Not Started" if none of the above conditions are met
  return { label: "Not Started", color: "default" };
};

const POLICY_TYPES = [
  "Term Life",
  "Whole Life",
  "Universal Life",
  "Variable Life",
  "Group Life",
  "Other",
];

const PREMIUM_FREQUENCIES = ["Monthly", "Quarterly", "Semi-Annual", "Annual"];

const ADDITIONAL_COVERAGE_TYPES = [
  "Disability",
  "Long-term Care",
  "Critical Illness",
  "Accidental Death",
];

const InsuranceSection = ({
  data,
  onUpdate,
  memberId,
  householdId,
  hideTitle,
}) => {
  // Add logging for component props
  logger.info("InsuranceSection Props:", {
    memberId,
    householdId,
    data,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [activeModal, setActiveModal] = useState({
    type: null,
    view: null,
    data: null,
  });
  const [unifiedStatus, setUnifiedStatus] = useState({
    label: "Loading...",
    color: "default",
  });

  const [viewAllModal, setViewAllModal] = useState({
    open: false,
    type: null,
  });

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    type: null,
    id: null,
  });

  const [noneActiveConfirm, setNoneActiveConfirm] = useState(false);

  useEffect(() => {
    if (data) {
      logger.info("Insurance data received:", data);
      const status = getUnifiedInsuranceStatus(data, data.noneActive);
      logger.info("Calculated unified status:", status);
      setUnifiedStatus(status);
      setIsLoading(false);
    }
  }, [data]);

  const handleMarkNoneActive = async () => {
    try {
      const response = await api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${memberId}/health-life/insurance-status`,
        { noneActive: true },
      );

      if (response.data.success) {
        onUpdate({
          ...data,
          noneActive: true,
        });
      }
    } catch (error) {
      logger.error("Error marking insurance as none active:", error);
    }
  };

  const handleAddPolicy = () => {
    setActiveModal({
      type: "unified",
      view: "form",
      data: null,
    });
  };

  const getUnifiedMetrics = () => {
    if (!data) return [];

    // Helper function to check if a policy is active
    const isPolicyActive = (policy) => {
      if (!policy) return false;
      return (
        policy.isActive !== false &&
        (!policy.endDate || new Date(policy.endDate) > new Date())
      );
    };

    // Filter active policies
    const activeLifeInsurance = (data.lifeInsurance || []).filter(
      isPolicyActive,
    );
    const activeAdditionalCoverage = (data.additionalCoverage || []).filter(
      isPolicyActive,
    );

    // Calculate totals from active policies only
    const totalCoverage = [
      ...activeLifeInsurance,
      ...activeAdditionalCoverage,
    ].reduce((sum, policy) => sum + Number(policy.coverageAmount || 0), 0);

    const totalAnnualPremium = [
      ...activeLifeInsurance,
      ...activeAdditionalCoverage,
    ].reduce((sum, policy) => {
      if (!policy || !policy.premium) return sum;
      const premium = Number(policy.premium || policy.premiums || 0);
      switch (policy.premiumFrequency) {
        case "Monthly":
          return sum + premium * 12;
        case "Quarterly":
          return sum + premium * 4;
        case "Semi-Annual":
          return sum + premium * 2;
        case "Annual":
          return sum + premium;
        default:
          return sum;
      }
    }, 0);

    return [
      {
        label: "Total Policies",
        value: activeLifeInsurance.length + activeAdditionalCoverage.length,
      },
      { label: "Total Coverage", value: `$${totalCoverage.toLocaleString()}` },
      {
        label: "Annual Premium",
        value: `$${totalAnnualPremium.toLocaleString()}`,
      },
    ];
  };

  // Don't render anything while loading
  if (isLoading) {
    return null;
  }

  const handleModalOpen = (type, view = "list", data = null) => {
    const emptyLifePolicy = {
      policyName: "",
      policyNumber: "",
      type: "",
      provider: "",
      coverageAmount: "",
      cashSurrenderValue: "",
      premium: "",
      premiumFrequency: "",
      startDate: "",
      endDate: "",
      notes: "",
    };

    const emptyAdditionalCoverage = {
      type: "",
      provider: "",
      coverageAmount: "",
      premium: "",
      waitingPeriod: "",
      benefitPeriod: "",
      notes: "",
    };

    setActiveModal({
      type,
      view,
      data:
        view === "form"
          ? data ||
            (type === "life" ? emptyLifePolicy : emptyAdditionalCoverage)
          : data,
    });
  };

  const handleModalClose = () => {
    setActiveModal({
      type: null,
      view: null,
      data: null,
    });
  };

  const getLifeInsuranceMetrics = () => {
    if (!data?.lifeInsurance?.length) return [];

    const totalCoverage = data.lifeInsurance.reduce((sum, policy) => {
      if (!policy || !policy.coverageAmount) return sum;
      return sum + Number(policy.coverageAmount);
    }, 0);

    const totalAnnualPremium = data.lifeInsurance.reduce((sum, policy) => {
      if (!policy || !policy.premium) return sum;
      const premium = Number(policy.premium);
      switch (policy.premiumFrequency) {
        case "Monthly":
          return sum + premium * 12;
        case "Quarterly":
          return sum + premium * 4;
        case "Semi-Annual":
          return sum + premium * 2;
        case "Annual":
          return sum + premium;
        default:
          return sum;
      }
    }, 0);

    return [
      { label: "Total Policies", value: data.lifeInsurance.length },
      { label: "Total Coverage", value: `$${totalCoverage.toLocaleString()}` },
      {
        label: "Annual Premium",
        value: `$${totalAnnualPremium.toLocaleString()}`,
      },
      {
        label: "Active Policies",
        value: data.lifeInsurance.filter(
          (p) => p && p.endDate && new Date(p.endDate) > new Date(),
        ).length,
      },
    ];
  };

  const getAdditionalCoverageMetrics = () => {
    if (!data?.additionalCoverage?.length) return [];

    const totalCoverage = data.additionalCoverage.reduce(
      (sum, coverage) => sum + Number(coverage.coverageAmount || 0),
      0,
    );

    const totalAnnualPremium = data.additionalCoverage.reduce(
      (sum, coverage) => sum + Number(coverage.premium || 0),
      0,
    );

    return [
      { label: "Total Coverage", value: `$${totalCoverage.toLocaleString()}` },
      {
        label: "Annual Premium",
        value: `$${totalAnnualPremium.toLocaleString()}`,
      },
      { label: "Policies", value: data.additionalCoverage.length },
    ];
  };

  const handleUpdateLifeInsurance = (policy) => {
    if (!policy) return;

    const newLifeInsurance = [...(data?.lifeInsurance || [])];
    if (activeModal.data && activeModal.data.id) {
      // Editing existing policy
      const index = newLifeInsurance.findIndex(
        (p) => p.id === activeModal.data.id,
      );
      if (index !== -1) {
        newLifeInsurance[index] = policy;
      }
    } else {
      // Adding new policy
      newLifeInsurance.push({
        ...policy,
        id: Date.now(), // Generate a temporary ID
      });
    }

    onUpdate({
      ...data,
      lifeInsurance: newLifeInsurance,
    });
    handleModalClose();
  };

  const handleUpdateAdditionalCoverage = (coverage) => {
    if (!coverage) return;

    const newAdditionalCoverage = [...(data?.additionalCoverage || [])];
    if (activeModal.data && activeModal.data.id) {
      // Editing existing coverage
      const index = newAdditionalCoverage.findIndex(
        (c) => c.id === activeModal.data.id,
      );
      if (index !== -1) {
        newAdditionalCoverage[index] = coverage;
      }
    } else {
      // Adding new coverage
      newAdditionalCoverage.push({
        ...coverage,
        id: Date.now(), // Generate a temporary ID
      });
    }

    onUpdate({
      ...data,
      additionalCoverage: newAdditionalCoverage,
    });
    handleModalClose();
  };

  const handleDeleteConfirm = (type, id) => {
    setDeleteConfirm({
      open: true,
      type,
      id,
    });
  };

  const handleDeleteConfirmed = () => {
    if (deleteConfirm.type === "life") {
      handleDeleteLifeInsurance(deleteConfirm.id);
    } else {
      handleDeleteAdditionalCoverage(deleteConfirm.id);
    }
    setDeleteConfirm({ open: false, type: null, id: null });
  };

  const handleDeleteLifeInsurance = (policyId) => {
    const newLifeInsurance = data.lifeInsurance.filter(
      (p) => p.id !== policyId,
    );
    onUpdate({
      ...data,
      lifeInsurance: newLifeInsurance,
    });
  };

  const handleDeleteAdditionalCoverage = (coverageId) => {
    const newAdditionalCoverage = data.additionalCoverage.filter(
      (c) => c.id !== coverageId,
    );
    onUpdate({
      ...data,
      additionalCoverage: newAdditionalCoverage,
    });
  };

  return (
    <Box>
      {!hideTitle && (
        <Typography variant="h6" gutterBottom>
          Insurance Information
        </Typography>
      )}

      {!isLoading && (
        <SummaryCard
          title="Insurance Coverage"
          subtitle="View and manage all insurance policies"
          status={unifiedStatus.label}
          statusColor={unifiedStatus.color}
          metrics={getUnifiedMetrics()}
          onAction={() => {
            if (
              data?.lifeInsurance?.length ||
              data?.additionalCoverage?.length
            ) {
              setViewAllModal({ open: true, type: "unified" });
            } else {
              handleAddPolicy();
            }
          }}
          actionLabel={
            !data?.lifeInsurance?.length && !data?.additionalCoverage?.length
              ? "Add Coverage"
              : "View All/Edit"
          }
          secondaryAction={
            !data?.lifeInsurance?.length &&
            !data?.additionalCoverage?.length &&
            data?.noneActive !== true ? (
              <Button
                size="small"
                onClick={() => setNoneActiveConfirm(true)}
                startIcon={<AddIcon />}
              >
                I have no active policies
              </Button>
            ) : null
          }
        />
      )}

      {/* Confirm Dialog for "I have no active policies" */}
      <ConfirmDialog
        open={noneActiveConfirm}
        title="Confirm No Active Policies"
        message="Are you sure you want to mark this as having no active policies? This can be changed later if you add any policies."
        onConfirm={() => {
          handleMarkNoneActive();
          setNoneActiveConfirm(false);
        }}
        onCancel={() => setNoneActiveConfirm(false)}
      />

      {/* Unified View All Modal */}
      <Modal
        isOpen={viewAllModal.open}
        onClose={() => setViewAllModal({ open: false, type: null })}
        maxWidth="4xl"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <Typography variant="h6">Insurance Policies</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddPolicy}
              variant="contained"
              color="primary"
            >
              Add New Coverage
            </Button>
          </div>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell align="right">Coverage Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ...(data?.lifeInsurance || []),
                ...(data?.additionalCoverage || []),
              ].map((policy, index) => (
                <TableRow key={index}>
                  <TableCell>{policy.type}</TableCell>
                  <TableCell>{policy.provider}</TableCell>
                  <TableCell align="right">
                    ${Number(policy.coverageAmount).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        policy.isActive !== false &&
                        (!policy.endDate ||
                          new Date(policy.endDate) > new Date())
                          ? "Active"
                          : "Inactive"
                      }
                      color={
                        policy.isActive !== false &&
                        (!policy.endDate ||
                          new Date(policy.endDate) > new Date())
                          ? "success"
                          : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setActiveModal({
                            type: policy.type.includes("Life")
                              ? "life"
                              : "additional",
                            view: "form",
                            data: policy,
                          });
                          setViewAllModal({ open: false, type: null });
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDeleteConfirm(
                            policy.type.includes("Life")
                              ? "life"
                              : "additional",
                            policy.id,
                          )
                        }
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setViewAllModal({ open: false, type: null })}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Policy Edit Modal */}
      <Modal
        isOpen={activeModal.type !== null}
        onClose={() => setActiveModal({ type: null, view: null, data: null })}
        maxWidth="2xl"
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-6">
            {activeModal.data?.id ? "Edit Policy" : "Add New Policy"}
          </h2>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Policy Type</InputLabel>
                <Select
                  value={activeModal.data?.type || ""}
                  onChange={(e) =>
                    setActiveModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, type: e.target.value },
                    }))
                  }
                  label="Policy Type"
                >
                  {POLICY_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Policy Name"
                value={activeModal.data?.policyName || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, policyName: e.target.value },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Policy Number"
                value={activeModal.data?.policyNumber || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, policyNumber: e.target.value },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Policy Type</InputLabel>
                <Select
                  value={activeModal.data?.type || ""}
                  onChange={(e) =>
                    setActiveModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, type: e.target.value },
                    }))
                  }
                  label="Policy Type"
                >
                  {POLICY_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Provider"
                value={activeModal.data?.provider || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, provider: e.target.value },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Coverage Amount"
                type="number"
                value={activeModal.data?.coverageAmount || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, coverageAmount: e.target.value },
                  }))
                }
                InputProps={{
                  startAdornment: (
                    <Box component="span" sx={{ mr: 1 }}>
                      $
                    </Box>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cash Surrender Value"
                type="number"
                value={activeModal.data?.cashSurrenderValue || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, cashSurrenderValue: e.target.value },
                  }))
                }
                InputProps={{
                  startAdornment: (
                    <Box component="span" sx={{ mr: 1 }}>
                      $
                    </Box>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Premium"
                type="number"
                value={activeModal.data?.premium || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, premium: e.target.value },
                  }))
                }
                InputProps={{
                  startAdornment: (
                    <Box component="span" sx={{ mr: 1 }}>
                      $
                    </Box>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Premium Frequency</InputLabel>
                <Select
                  value={activeModal.data?.premiumFrequency || ""}
                  onChange={(e) =>
                    setActiveModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, premiumFrequency: e.target.value },
                    }))
                  }
                  label="Premium Frequency"
                >
                  {PREMIUM_FREQUENCIES.map((freq) => (
                    <MenuItem key={freq} value={freq}>
                      {freq}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={activeModal.data?.isActive !== false}
                  onChange={(e) =>
                    setActiveModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, isActive: e.target.value },
                    }))
                  }
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Start Date"
                type="date"
                value={activeModal.data?.startDate || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, startDate: e.target.value },
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={activeModal.data?.endDate || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, endDate: e.target.value },
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={activeModal.data?.notes || ""}
                onChange={(e) =>
                  setActiveModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, notes: e.target.value },
                  }))
                }
              />
            </Grid>
          </Grid>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={() =>
                setActiveModal({ type: null, view: null, data: null })
              }
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const isLifeInsurance =
                  activeModal.data?.type &&
                  POLICY_TYPES.includes(activeModal.data.type);
                if (isLifeInsurance) {
                  handleUpdateLifeInsurance(activeModal.data);
                } else {
                  handleUpdateAdditionalCoverage(activeModal.data);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800"
            >
              Save
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirm Dialog for Delete */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Confirm Delete"
        message={`Are you sure you want to delete this ${deleteConfirm.type === "life" ? "life insurance policy" : "coverage"}?`}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setDeleteConfirm({ open: false, type: null, id: null })}
      />
    </Box>
  );
};

export default InsuranceSection;
